/**
 * PavilionCoordinator
 *
 * Manages Pavilion-specific features including matchmaking, ad mode, and campaign management.
 *
 * Responsibilities:
 * - Matchmaking state management (queued, searching, found, cancelled)
 * - Registering message handlers for realtime matchmaking messages
 * - Emitting semantic events for matchmaking state changes
 * - Providing public API for reading matchmaking state
 *
 * This is the FIRST coordinator implemented using the strangler-fig pattern.
 */

import { makeStateStore } from '@playcastdotio/StateStore';
import type { IMessageRouter } from '../../services/MessageRouter';
import {
  MatchmakingMessage,
  MatchmakingMessageType,
  Invitation,
  websocket,
  HostOperationalState,
  HostGameState,
  HostCampaignConfig,
  MatchmakeableBlockReason,
} from '@playcastdotio/Messaging';
import type { ShadowcamHealthSource } from '../media/managers/ShadowcamPresenceMonitor';
import type { SanitizationHealthSource } from './NonsoleSanitizationMonitor';
import { MatchmakingManager } from './managers/MatchmakingManager';
import { CafeInvitationManager } from './managers/CafeInvitationManager';
import {
  MatchmakingCooldownManager,
  CooldownChangedEvent,
} from './managers/MatchmakingCooldownManager';
import type { IRealtimeService } from '../../services/realtime/IRealtimeService';
import type { ICampaignConfigService } from '../../services/campaign/ICampaignConfigService';
import type { SessionCoordinator } from '../session/SessionCoordinator';
import type { GameLaunchCoordinator } from '../game-launch/GameLaunchCoordinator';
import type { AppCoordinator } from '../app/AppCoordinator';
import { machineIdState } from '../../api/stores/shared';
import { CoordinatorEventImpl } from '../_types/events';
import type { IHostAnalyticsManager } from '../../services/analytics/IHostAnalyticsManager';
import * as Sentry from '@sentry/react';

// ========================================
// Types
// ========================================

/**
 * Base timeout for prewarming safety (matches CampaignSwitchManager).
 * If host is stuck in prewarming after a restart, force cleanup after this + readyWaitTime.
 */
const PREWARMING_SAFETY_BASE_TIMEOUT_MS = 480000; // 8 minutes

export type MatchmakingStatus = 'idle' | 'queued' | 'searching' | 'found' | 'cancelled';

export type MatchmakingState = {
  status: MatchmakingStatus;
  matchId?: string;
};

export type InvitationState = Invitation;

export type CampaignState = {
  campaignId?: string;
  reason?: string;
  config?: HostCampaignConfig; // Full campaign configuration from API
  configFetched?: boolean; // Track if config has been fetched
};

export type SteamCredentialsState = {
  username: string;
  password: string;
};

// Use full HostOperationalState for API compatibility
export type HostMatchmakingState = HostOperationalState;

// ========================================
// PavilionCoordinator
// ========================================

export class PavilionCoordinator {
  private matchmakingStore: ReturnType<typeof makeStateStore<MatchmakingState>>;
  private invitationStore: ReturnType<typeof makeStateStore<InvitationState>>;
  private campaignStore: ReturnType<typeof makeStateStore<CampaignState>>;
  private credentialsStore: ReturnType<typeof makeStateStore<SteamCredentialsState>>;
  private hostMatchmakingStore: ReturnType<typeof makeStateStore<HostMatchmakingState>>;

  private realtimeService: IRealtimeService;
  private campaignConfigService: ICampaignConfigService;
  // In-flight `refreshCampaignConfig()` request, shared by concurrent callers so
  // a burst of player joins triggers one fetch rather than one per player.
  private campaignConfigRefresh?: Promise<void>;
  private analytics: IHostAnalyticsManager;
  private appCoordinator?: AppCoordinator; // Set during init for reading static properties
  private sessionCoordinator?: SessionCoordinator; // Set during init for stream lifecycle
  private gameLaunchCoordinator?: GameLaunchCoordinator; // Set during init for macro execution
  private isRestarting = false; // Guards handleGameClosed from clearing campaign during restart
  private isSwitchingCampaign = false; // Guards handleGameClosed from clearing campaign during switch
  // Guards handleGameClosed from clearing campaign while StatefulMacroCoordinator is
  // mid-retry (stopGame → await gameClosed → relaunch with the same campaignId).
  // Set via beginMacroRetryRestart() before the stopGame call; cleared on the next
  // successful gameLaunched (alongside the other guards) and in every code path
  // that resets the host to idle.
  private isMacroRestarting = false;
  private prewarmingSafetyTimer: ReturnType<typeof setTimeout> | null = null;
  private isAdminSessionActive = false;
  // Sticky terminal gate set when the admin exits with a destructive action
  // (reboot / exit Nonsole). While true, the host is permanently non-matchmakeable
  // so it cannot re-enter the pool in the window before PlaycastService finishes the
  // guest-mode exit / reboot. This is intentionally never cleared in-process: a late
  // or in-flight relaunch must NOT drop the gate and re-expose the host. The flag's
  // lifetime is the process lifetime, which ends as part of teardown. If guest-mode
  // exit somehow fails, staying non-matchmakeable is the safe failure mode.
  private isTerminalExit = false;
  private shadowcamHealthSource: ShadowcamHealthSource | null = null;
  private shadowcamHealthUnsubscribe: (() => void) | null = null;
  private sanitizationHealthSource: SanitizationHealthSource | null = null;
  private sanitizationHealthUnsubscribe: (() => void) | null = null;

  private managers: {
    matchmaking: MatchmakingManager;
    cafeInvitation: CafeInvitationManager;
    cooldown: MatchmakingCooldownManager;
  };

  // Tracks the launch instance the current cooldown decision is bound to.
  // Generated when the game becomes ready; cleared when the game closes / campaign clears.
  // Phase 3 of docs/feature-plans/matchmaking-cooldown.
  private currentLaunchInstanceId: string | null = null;

  // Auth ID (temporary - will be owned by IdentityCoordinator in the future)
  // TODO-STRANGLER-FIG: Move to IdentityCoordinator
  private authId = '';

  // Track previous invitation state for change detection
  private previousInvitation: Invitation | null = null;

  public events = {
    // Matchmaking events
    matchmakingStarted: new CoordinatorEventImpl<void>(),
    matchFound: new CoordinatorEventImpl<{ matchId: string }>(),
    matchmakingCancelled: new CoordinatorEventImpl<void>(),
    matchmakingError: new CoordinatorEventImpl<{ code: string; message: string }>(),

    // Campaign events
    campaignAssigned: new CoordinatorEventImpl<{ campaignId: string }>(),
    campaignConfigLoaded: new CoordinatorEventImpl<{ campaignId: string }>(),
    campaignCleared: new CoordinatorEventImpl<void>(),

    // Host matchmaking state events
    matchmakeableChanged: new CoordinatorEventImpl<{ matchmakeable: boolean }>(),
    gameStateChanged: new CoordinatorEventImpl<{ gameState: HostGameState }>(),

    // Cafe invitation events
    invitationInvalidated: new CoordinatorEventImpl<{ invitationId: string; reason?: string }>(),
    softKickExpired: new CoordinatorEventImpl<{ invitationId: string }>(),
    lobbyOwnerChanged: new CoordinatorEventImpl<{ oldOwnerId: string; newOwnerId: string }>(),
    steamGuestLoginChanged: new CoordinatorEventImpl<{ enabled: boolean }>(),
  };

  constructor(realtimeService: IRealtimeService, campaignConfigService: ICampaignConfigService, analytics: IHostAnalyticsManager) {
    this.realtimeService = realtimeService;
    this.campaignConfigService = campaignConfigService;
    this.analytics = analytics;

    // Initialize state stores
    this.matchmakingStore = makeStateStore<MatchmakingState>({
      status: 'idle',
      matchId: undefined,
    });

    // Initialize with empty cafe invitation (will be updated when match is found)
    this.invitationStore = makeStateStore<InvitationState>({
      id: '',
      type: 'cafe',
      invalid: false,
      linkExpiry: '',
      maxLinkUse: -1,
      authRequired: false,
      visibility: 'public',
      owner: {
        avatar: '',
        profileId: '',
        username: '',
      },
      manifest: {
        players: [],
        matchmakeToPublicMachine: false,
      },
    });

    this.campaignStore = makeStateStore<CampaignState>({
      campaignId: undefined,
      reason: undefined,
      config: undefined,
      configFetched: false,
    });

    this.credentialsStore = makeStateStore<SteamCredentialsState>({
      username: '',
      password: '',
    });

    this.hostMatchmakingStore = makeStateStore<HostMatchmakingState>({
      globalMatchmakingToggle: true,
      matchmakeable: false, // Start as not matchmakeable until game launches
      bandwidth: { uploadMbps: 100, downloadMbps: 300, lastUpdated: 0 },
      // Static properties (isCloudHost, guestAccount) are read from
      // AppCoordinator at API send time, but we initialize with defaults here
      guestAccount: false,
      isCloudHost: false, // Overridden by AppCoordinator in sendHostConfigToAPI()
      gameState: 'idle',
      gameStateChangedAt: Date.now(),
      campaignId: undefined,
      lastSwitchedToCampaign: undefined,
    }, {
      // Replicate to IPC consumers (admin dashboard, host backend, node service)
      // Uses same channel as legacy hostMatchmakingState so existing consumers receive updates
      replicate: { channel: 'hostMatchmakingState', targets: ['remoteClient', 'hostBackend', 'nodeService'] },
    });

    // Initialize managers
    this.managers = {
      matchmaking: new MatchmakingManager(
        {
          matchmaking: this.matchmakingStore,
          invitation: this.invitationStore,
          campaign: this.campaignStore,
          credentials: this.credentialsStore,
        },
        this.events,
        this.analytics,
      ),
      cafeInvitation: new CafeInvitationManager(
        {
          invitation: this.invitationStore,
          hostMatchmaking: this.hostMatchmakingStore,
          matchmaker: this.matchmakingStore,
          // TODO-STRANGLER-FIG: Add player and host stores when migrating those domains
          player: makeStateStore({ players: {} }),
          host: makeStateStore({ status: 'idle', edition: 'pavilion' }),
        },
        this.events,
      ),
      cooldown: new MatchmakingCooldownManager(),
    };

    // Cooldown lifecycle hooks. The manager is wall-clock authoritative — we just
    // mirror its transitions into the operational state and push HOST_CONFIG.
    this.managers.cooldown.events.changed.addListener((event) => {
      this.handleCooldownChanged(event);
    });
  }

  /**
   * Inject the collaborators this coordinator delegates to.
   *
   * `init()` calls this to store the production collaborators before wiring
   * their event listeners, keeping a single place that assigns the references.
   * It is also the supported way for focused tests to supply test doubles for
   * the SessionCoordinator / GameLaunchCoordinator that action methods (e.g.
   * restartGameForAdminExit) delegate to, without driving the full init()
   * listener graph. Only the collaborators that are provided are replaced;
   * listener wiring remains the responsibility of init().
   */
  public setCollaborators(collaborators: {
    appCoordinator?: AppCoordinator;
    gameLaunchCoordinator?: GameLaunchCoordinator;
    sessionCoordinator?: SessionCoordinator;
  }): void {
    if (collaborators.appCoordinator) {
      this.appCoordinator = collaborators.appCoordinator;
    }
    if (collaborators.gameLaunchCoordinator) {
      this.gameLaunchCoordinator = collaborators.gameLaunchCoordinator;
    }
    if (collaborators.sessionCoordinator) {
      this.sessionCoordinator = collaborators.sessionCoordinator;
    }
  }

  /**
   * Initialize the coordinator
   *
   * @param appCoordinator - AppCoordinator for reading static host properties
   * @param gameLaunchCoordinator - Optional GameLaunchCoordinator to listen to for campaign switch events
   * @param sessionCoordinator - Optional SessionCoordinator for stream lifecycle management
   */
  public init(
    appCoordinator: AppCoordinator,
    gameLaunchCoordinator?: GameLaunchCoordinator,
    sessionCoordinator?: SessionCoordinator
  ): void {
    console.log('[PavilionCoordinator] Initialized');

    // Store references to other coordinators (single assignment point)
    this.setCollaborators({ appCoordinator, gameLaunchCoordinator, sessionCoordinator });

    // Sync guestAccount and isCloudHost from AppCoordinator to hostMatchmakingStore.
    // The node service reads guestAccount from the replicated store for heartbeat attribution,
    // so it must reflect the actual value (not the default 'false').
    this.hostMatchmakingStore.updateState({
      ...this.hostMatchmakingStore.currentState,
      guestAccount: appCoordinator.getGuestAccount(),
      isCloudHost: appCoordinator.getIsCloudHost(),
    });

    // Initialize managers
    this.managers.matchmaking.init();
    this.managers.cafeInvitation.init();

    // Send initial HOST_CONFIG to register with matchmaker
    // (WebSocket is already connected by the time coordinators initialize)
    console.log('[PavilionCoordinator] 📤 Sending initial HOST_CONFIG on init');
    this.sendHostConfigToAPI();

    // Listen for WebSocket reconnection events
    // Send HOST_CONFIG whenever we reconnect to re-register with matchmaker
    this.realtimeService.onConnected(() => {
      console.log('[PavilionCoordinator] 🌐 WebSocket reconnected - sending HOST_CONFIG to re-register with matchmaker');

      // Force idle if prewarming is stale (host was disconnected for a long time)
      const currentState = this.hostMatchmakingStore.currentState;
      if (currentState.gameState === 'prewarming' && currentState.gameStateChangedAt) {
        const prewarmingAge = Date.now() - currentState.gameStateChangedAt;
        if (prewarmingAge > PREWARMING_SAFETY_BASE_TIMEOUT_MS) {
          console.error(`[PavilionCoordinator] ⚠️ Stale prewarming detected on reconnect (${Math.round(prewarmingAge / 1000)}s old) — forcing idle`);

          // Clear campaign store (prevents stale getCampaignId() after reconnect)
          this.campaignStore.updateState({
            campaignId: undefined,
            reason: undefined,
            config: undefined,
            configFetched: false,
          });

          this.managers.cooldown.clear('reconnect_stale_prewarming');
          this.currentLaunchInstanceId = null;
          // Re-read state AFTER cooldown.clear() — its 'cleared' listener
          // scrubs cooldown-derived fields (e.g. `matchmakeableBlockReason:
          // 'cooldown'`) from the store. Spreading the pre-clear `currentState`
          // would reintroduce them and ship a contradictory HOST_CONFIG that
          // claims `gameState: 'idle'` while still flagging `cooldown` as the
          // matchmakeable block reason.
          const postClearState = this.hostMatchmakingStore.currentState;
          this.hostMatchmakingStore.updateState({
            ...postClearState,
            gameState: 'idle',
            gameStateChangedAt: Date.now(),
            matchmakeable: false,
            campaignId: undefined,
            cooldownEndsAt: undefined,
          });
          this.isRestarting = false;
          this.isSwitchingCampaign = false;
          this.isMacroRestarting = false;
          this.clearPrewarmingSafetyTimeout();
        }
      }

      this.sendHostConfigToAPI();
    });

    // Listen for machineId initialization
    // Send HOST_CONFIG once machineId becomes available (in case it wasn't ready at init time)
    let machineIdInitialized = false;
    machineIdState.addListener((machineId) => {
      if (machineId && !machineIdInitialized) {
        machineIdInitialized = true;
        console.log('[PavilionCoordinator] 🆔 Machine ID initialized - sending HOST_CONFIG');
        this.sendHostConfigToAPI();
      }
    });

    // Listen to campaignAssigned event to fetch campaign config
    this.events.campaignAssigned.addListener(async (data: { campaignId: string }) => {
      console.log('[PavilionCoordinator] Campaign assigned, fetching config:', data.campaignId);
      Sentry.logger.info(`[PavilionCoordinator] Campaign assigned, fetching config: ${data.campaignId}`);

      // Set switching guard BEFORE any async work — prevents handleGameClosed from
      // clearing the campaign when Playjector terminates the old game during the switch
      this.isSwitchingCampaign = true;

      // Fetch campaign config from API
      const config = await this.fetchCampaignConfig(data.campaignId);

      if (config) {
        console.log('[PavilionCoordinator] 📦 Raw config from API:', JSON.stringify(config, null, 2));
        Sentry.logger.info(`[PavilionCoordinator] 📦 Raw config from API: ${JSON.stringify(config, null, 2)}`);

        // Update campaign store with fetched config
        this.campaignStore.updateState({
          ...this.campaignStore.currentState,
          config,
          configFetched: true,
        });

        console.log('[PavilionCoordinator] ✅ Campaign config stored:', config.displayName);
        Sentry.logger.info(`[PavilionCoordinator] ✅ Campaign config stored: ${config.displayName}`);

        // Update matchmaking state with assigned campaign
        // This puts us in "prewarming" state so dashboard shows we're switching
        // Any active cooldown belongs to the previous launch and is now invalid.
        this.managers.cooldown.clear('campaign_switch_begin');
        this.currentLaunchInstanceId = null;
        this.hostMatchmakingStore.updateState({
          ...this.hostMatchmakingStore.currentState,
          campaignId: config.campaignId,
          gameState: 'prewarming',
          gameStateChangedAt: Date.now(),
          matchmakeable: false, // Not matchmakeable until game launches
          cooldownEndsAt: undefined,
        });

        // Start prewarming safety timeout (defense-in-depth against stuck prewarming)
        this.startPrewarmingSafetyTimeout(config.campaignId, config.launchPackage?.readyWaitTimeMs ?? 0);

        // Send HOST_CONFIG to API to notify of campaign switch START
        console.log('[PavilionCoordinator] 📤 Notifying API of campaign switch start:', config.campaignId);
        Sentry.logger.info(`[PavilionCoordinator] 📤 Notifying API of campaign switch start: ${config.campaignId}`);
        this.sendHostConfigToAPI();

        // Emit event for other coordinators (e.g., GameLaunchCoordinator)
        this.events.campaignConfigLoaded.emit({ campaignId: config.campaignId });
      } else {
        console.error('[PavilionCoordinator] ❌ Failed to fetch campaign config:', data.campaignId);
        Sentry.logger.error(`[PavilionCoordinator] ❌ Failed to fetch campaign config: ${data.campaignId}`)

        this.isSwitchingCampaign = false;

        this.campaignStore.updateState({
          ...this.campaignStore.currentState,
          configFetched: false,
        });

        this.handleCampaignSwitchFailure(data.campaignId, 'Failed to fetch campaign config');
      }
    });

    // Listen to campaignCleared event (API requested campaign removal)
    this.events.campaignCleared.addListener(() => {
      console.log('[PavilionCoordinator] Campaign cleared by API, stopping game and resetting state');
      Sentry.logger.info('[PavilionCoordinator] Campaign cleared by API, stopping game and resetting state');
      this.handleCampaignCleared();
    });

    // Listen to GameLaunchCoordinator events (if provided)
    if (gameLaunchCoordinator) {
      console.log('[PavilionCoordinator] Setting up GameLaunchCoordinator event listeners');
      Sentry.logger.info('[PavilionCoordinator] Setting up GameLaunchCoordinator event listeners');

      gameLaunchCoordinator.events.campaignSwitchSucceeded.addListener((data: { campaignId: string }) => {
        console.log('[PavilionCoordinator] Campaign switch succeeded:', data.campaignId);
        Sentry.logger.info('[PavilionCoordinator] Campaign switch succeeded:', { campaignId: data.campaignId });
        this.handleCampaignSwitchSuccess(data.campaignId);
      });

      gameLaunchCoordinator.events.campaignSwitchFailed.addListener((data: { campaignId: string; reason: string; transient?: boolean }) => {
        console.log('[PavilionCoordinator] Campaign switch failed:', data.campaignId, data.reason);
        Sentry.logger.info('[PavilionCoordinator] Campaign switch failed:', { campaignId: data.campaignId, reason: data.reason });
        this.handleCampaignSwitchFailure(data.campaignId, data.reason, data.transient ?? false);
      });

      // When game is ready (launch macro completed) → set matchmakeable = true
      gameLaunchCoordinator.events.gameReady.addListener((data: { gameId: number; gameName: string }) => {
        console.log('[PavilionCoordinator] Game ready (macro complete), setting matchmakeable:', data.gameName);
        Sentry.logger.info('[PavilionCoordinator] Game ready (macro complete), setting matchmakeable:', { gameName: data.gameName });
        this.handleGameLaunched();
      });

      // When game closes → clear campaign and notify API
      gameLaunchCoordinator.events.gameClosed.addListener(() => {
        console.log('[PavilionCoordinator] Game closed, clearing campaign and updating API');
        Sentry.logger.info('[PavilionCoordinator] Game closed, clearing campaign and updating API');
        this.handleGameClosed();
      });
    }

    // Listen to matchFound event and orchestrate stream start
    if (sessionCoordinator) {
      console.log('[PavilionCoordinator] Setting up SessionCoordinator orchestration');
      Sentry.logger.info('[PavilionCoordinator] Setting up SessionCoordinator orchestration');

      this.events.matchFound.addListener(async (data: { matchId: string }) => {
        console.log('[PavilionCoordinator] Match found, starting stream for match:', data.matchId);
        Sentry.logger.info('[PavilionCoordinator] Match found, starting stream for match:', { matchId: data.matchId });
        try {
          // Await join macro BEFORE starting stream (legacy parity: JoinMacro -> HostReady -> StartStream)
          // HOST_READY is sent inside startStream(), so the player cannot connect until macro finishes
          if (this.gameLaunchCoordinator) {
            await this.gameLaunchCoordinator.executeOnJoinMacro();
          }

          await sessionCoordinator.startStream(data.matchId, this.getCampaignDetails().campaignId);
          console.log('[PavilionCoordinator] ✅ Stream started for match:', data.matchId);
          Sentry.logger.info('[PavilionCoordinator] ✅ Stream started for match:', { matchId: data.matchId });
        } catch (error) {
          console.error('[PavilionCoordinator] ❌ Failed to start stream for match:', error);
          Sentry.logger.error('[PavilionCoordinator] ❌ Failed to start stream for match:', { error });
        }
      });

      // Listen to peerConnectionTimeout event to re-enter matchmaking pool
      sessionCoordinator.events.peerConnectionTimeout.addListener((data) => {
        console.log('[PavilionCoordinator] Peer connection timeout, re-entering matchmaking pool');
        Sentry.logger.info('[PavilionCoordinator] Peer connection timeout, re-entering matchmaking pool');
        this.handlePeerConnectionTimeout(data.invitationId);
      });

      // Listen to streamStopped to re-enter matchmaking pool when stream ends
      sessionCoordinator.events.streamStopped.addListener((data) => {
        console.log('[PavilionCoordinator] Stream stopped:', data.reason);
        Sentry.logger.info('[PavilionCoordinator] Stream stopped:', { reason: data.reason });
        this.handleStreamStopped(data.reason);
      });

      // Listen to allPlayersLeft to handle session cleanup and optional game restart
      sessionCoordinator.events.allPlayersLeft.addListener(() => {
        console.log('[PavilionCoordinator] 🚪 All players left while hosting');
        Sentry.logger.info('[PavilionCoordinator] 🚪 All players left while hosting');
        this.handleAllPlayersLeft();
      });
    }
  }

  /**
   * Handle successful campaign switch from PlayjectorCoordinator
   * Updates matchmaking state and notifies API
   */
  private handleCampaignSwitchSuccess(campaignId: string): void {
    console.log('[PavilionCoordinator] 📤 Handling campaign switch success:', campaignId);
    Sentry.logger.info('[PavilionCoordinator] 📤 Handling campaign switch success:', { campaignId });

    // Clear campaign switch guard
    this.isSwitchingCampaign = false;

    const currentState = this.hostMatchmakingStore.currentState;

    // Check if game has already launched (gameLaunched event may fire before pavilionLaunchReply SUCCESS)
    // Only check gameState — don't require matchmakeable===true, because a race condition during
    // campaign switches can leave matchmakeable=false even though the game IS running.
    const isGameAlreadyRunning = currentState.gameState === 'ready';

    if (isGameAlreadyRunning) {
      // Game is already running — update campaignId and recalculate matchmakeability
      this.hostMatchmakingStore.updateState({
        ...currentState,
        campaignId,
        lastSwitchedToCampaign: Date.now(),
      });
      const result = this.calculateMatchmakeabilityResult();
      this.hostMatchmakingStore.updateState({
        ...this.hostMatchmakingStore.currentState,
        matchmakeable: result.matchmakeable,
        matchmakeableBlockReason: result.blockReason,
      });
      console.log('[PavilionCoordinator] 🎮 SUCCESS: Game already running, matchmakeable:', result.matchmakeable, 'blockReason:', result.blockReason);
      Sentry.logger.info('[PavilionCoordinator] 🎮 SUCCESS: Game already running, matchmakeable:', { matchmakeable: result.matchmakeable, blockReason: result.blockReason });
    } else {
      // Game not launched yet, set prewarming state
      console.log('[PavilionCoordinator] ⏳ SUCCESS: Game not yet launched, sending HOST_CONFIG with gameState=prewarming, matchmakeable=false');
      Sentry.logger.info('[PavilionCoordinator] ⏳ SUCCESS: Game not yet launched, sending HOST_CONFIG with gameState=prewarming, matchmakeable=false');
      this.hostMatchmakingStore.updateState({
        ...currentState,
        campaignId,
        lastSwitchedToCampaign: Date.now(),
        gameState: 'prewarming',
        gameStateChangedAt: Date.now(),
        matchmakeable: false, // Will be set to true when game actually launches
      });
    }

    // Send HOST_CONFIG to API with updated state
    this.sendHostConfigToAPI();

    console.log('[PavilionCoordinator] ✅ Campaign switch success reported to API');
    Sentry.logger.info('[PavilionCoordinator] ✅ Campaign switch success reported to API');
  }

  /**
   * Handle failed campaign switch from PlayjectorCoordinator
   * Sends failure message to API and resets matchmaking state
   */
  private handleCampaignSwitchFailure(campaignId: string, reason: string, transient?: boolean): void {
    console.error('[PavilionCoordinator] ❌ FAILURE: Handling campaign switch failure:', campaignId, reason);
    Sentry.logger.error('[PavilionCoordinator] ❌ FAILURE: Handling campaign switch failure:', { campaignId, reason });

    // Clear campaign switch guard
    this.isSwitchingCampaign = false;

    // Clear restart guard and safety timer (may have been set by handleAllPlayersLeft restart path)
    this.isRestarting = false;
    // Mirror — a campaign-switch failure is terminal for the current campaign,
    // so any lingering macro-retry guard must drop with the other guards.
    this.isMacroRestarting = false;
    this.clearPrewarmingSafetyTimeout();

    // Clear campaign store only if the failing campaign is still the active one.
    // During A→B switches, A's "superseded" failure must NOT wipe B's config.
    if (this.campaignStore.currentState.campaignId === campaignId) {
      this.campaignStore.updateState({
        campaignId: undefined,
        reason: undefined,
        config: undefined,
        configFetched: false,
      });
    }

    // Send CAMPAIGN_SWITCH_FAILURE message to API
    const failureMessage = websocket.generators.matchmaking(
      MatchmakingMessageType.MATCHMAKER_CAMPAIGN_SWITCH_FAILURE,
      {
        campaignId,
        reason,
        machineName: machineIdState.currentState,
        transient: transient ?? false, // Permanent failure
      }
    );
    this.realtimeService.sendMessage(failureMessage);
    console.log('[PavilionCoordinator] ✅ Sent MATCHMAKER_CAMPAIGN_SWITCH_FAILURE to API');
    Sentry.logger.info('[PavilionCoordinator] ✅ Sent MATCHMAKER_CAMPAIGN_SWITCH_FAILURE to API');

    // Cooldown belongs to the launch instance that just failed.
    this.managers.cooldown.clear('campaign_switch_failure');
    this.currentLaunchInstanceId = null;

    // Update our matchmaking state (clear campaign, back to idle)
    // Keep matchmakeable false since game didn't launch successfully
    console.log('[PavilionCoordinator] 📤 FAILURE: Sending HOST_CONFIG with gameState=idle, matchmakeable=false, campaignId=undefined');
    Sentry.logger.info('[PavilionCoordinator] 📤 FAILURE: Sending HOST_CONFIG with gameState=idle, matchmakeable=false, campaignId=undefined');
    this.hostMatchmakingStore.updateState({
      ...this.hostMatchmakingStore.currentState,
      campaignId: undefined,
      gameState: 'idle',
      gameStateChangedAt: Date.now(),
      matchmakeable: false,
      cooldownEndsAt: undefined,
    });

    // Re-register with matchmaker (send updated HOST_CONFIG)
    this.sendHostConfigToAPI();

    console.log('[PavilionCoordinator] ✅ Re-registered with matchmaker after failure');
    Sentry.logger.info('[PavilionCoordinator] ✅ Re-registered with matchmaker after failure');
  }

  /**
   * Calculate whether the host should be matchmakeable based on current state.
   *
   * Conditions for matchmakeability:
   * - Global matchmaking toggle is enabled
   * - A game is actually running (gameState === 'ready')
   * - A campaign is assigned (campaignId is set)
   * - No admin session is active
   *
   * Use this instead of hardcoding matchmakeable=true to prevent invalid states.
   */
  private calculateMatchmakeability(): boolean {
    return this.calculateMatchmakeabilityResult().matchmakeable;
  }

  private calculateMatchmakeabilityResult(): {
    matchmakeable: boolean;
    blockReason: MatchmakeableBlockReason | null;
  } {
    const state = this.hostMatchmakingStore.currentState;

    // Terminal gate: the admin exited with a destructive action (reboot / exit
    // Nonsole). The host is tearing down, so it must never re-enter the pool —
    // this wins over every other (re)compute until the process is gone.
    if (this.isTerminalExit) {
      console.log('[PavilionCoordinator] 🧮 Calculated matchmakeability: false (host_exiting)');
      return { matchmakeable: false, blockReason: 'host_exiting' };
    }

    // Shadowcam gate first so its admin-visible reason wins over the generic "not ready" fallback.
    const shadowcamHealth = this.shadowcamHealthSource?.getHealth();
    if (shadowcamHealth && !shadowcamHealth.healthy) {
      console.log('[PavilionCoordinator] 🧮 Calculated matchmakeability: false (shadowcam_unhealthy)', shadowcamHealth);
      return { matchmakeable: false, blockReason: 'shadowcam_unhealthy' };
    }

    const sanitizationHealth = this.sanitizationHealthSource?.getHealth();
    if (sanitizationHealth?.isBetaHost && !sanitizationHealth.sanitized) {
      const blockReason = sanitizationHealth.blockReason ?? 'sanitization_failure';
      console.log('[PavilionCoordinator] 🧮 Calculated matchmakeability: false (sanitization)', {
        failureCode: sanitizationHealth.failureCode,
        blockReason,
      });
      return { matchmakeable: false, blockReason };
    }

    if (!state.globalMatchmakingToggle) {
      console.log('[PavilionCoordinator] 🧮 Calculated matchmakeability: false (global_toggle_off)');
      return { matchmakeable: false, blockReason: 'global_toggle_off' };
    }

    // Cooldown gate (host-side authority, v1). Wins over `game_not_ready` so the
    // admin-visible reason is precise — game IS running, we're just waiting on
    // the campaign-configured cooldown window. See docs/feature-plans/matchmaking-cooldown.
    if (this.managers.cooldown.isInCooldown()) {
      console.log('[PavilionCoordinator] 🧮 Calculated matchmakeability: false (cooldown)', {
        cooldownEndsAt: this.managers.cooldown.cooldownEndsAt(),
      });
      return { matchmakeable: false, blockReason: 'cooldown' };
    }

    if (state.gameState !== 'ready') {
      console.log('[PavilionCoordinator] 🧮 Calculated matchmakeability: false (game_not_ready)', { gameState: state.gameState });
      return { matchmakeable: false, blockReason: 'game_not_ready' };
    }

    if (!state.campaignId) {
      console.log('[PavilionCoordinator] 🧮 Calculated matchmakeability: false (no_campaign)');
      return { matchmakeable: false, blockReason: 'no_campaign' };
    }

    if (this.isAdminSessionActive) {
      console.log('[PavilionCoordinator] 🧮 Calculated matchmakeability: false (admin_session_active)');
      return { matchmakeable: false, blockReason: 'admin_session_active' };
    }

    console.log('[PavilionCoordinator] 🧮 Calculated matchmakeability: true');
    return { matchmakeable: true, blockReason: null };
  }

  /** Bind shadowcam health (owned by MediaCoordinator). Idempotent. */
  public setShadowcamHealthSource(source: ShadowcamHealthSource): void {
    this.shadowcamHealthUnsubscribe?.();
    this.shadowcamHealthSource = source;
    this.applyMatchmakeabilityResultAndPush('shadowcamHealthSourceBound');
    this.shadowcamHealthUnsubscribe = source.onHealthChange(() => {
      console.log('[PavilionCoordinator] 🩺 Shadowcam health changed — recomputing matchmakeability');
      this.applyMatchmakeabilityResultAndPush('shadowcamHealthChanged');
    });
  }

  /** Bind Nonsole sanitization health (replicated from PlaycastService). Idempotent. */
  public setSanitizationHealthSource(source: SanitizationHealthSource): void {
    this.sanitizationHealthUnsubscribe?.();
    this.sanitizationHealthSource = source;
    this.applyMatchmakeabilityResultAndPush('sanitizationHealthSourceBound');
    this.sanitizationHealthUnsubscribe = source.onHealthChange(() => {
      console.log('[PavilionCoordinator] 🛡️ Sanitization health changed — recomputing matchmakeability');
      this.applyMatchmakeabilityResultAndPush('sanitizationHealthChanged');
    });
  }

  private applyMatchmakeabilityResultAndPush(reason: string): void {
    const current = this.hostMatchmakingStore.currentState;
    const result = this.calculateMatchmakeabilityResult();
    const previousReason = current.matchmakeableBlockReason ?? null;
    const sanitizationHealth = this.sanitizationHealthSource?.getHealth();
    // Observe-only telemetry. The monitor reports `undefined` when the replicated
    // sanitization state has been cleared for an in-flight refresh; in that case we
    // PRESERVE the last-known values rather than emitting a premature zero count.
    const violationCount =
      sanitizationHealth?.processPolicyViolationCount ?? current.processPolicyViolationCount;
    const lastBlockedProcessName =
      sanitizationHealth?.lastBlockedProcessName ?? current.lastBlockedProcessName;
    const lastBlockedReason =
      sanitizationHealth?.lastBlockedReason ?? current.lastBlockedReason;

    const matchmakeabilityUnchanged =
      current.matchmakeable === result.matchmakeable && previousReason === result.blockReason;
    const observabilityUnchanged =
      (current.processPolicyViolationCount ?? 0) === (violationCount ?? 0) &&
      (current.lastBlockedProcessName ?? '') === (lastBlockedProcessName ?? '') &&
      (current.lastBlockedReason ?? '') === (lastBlockedReason ?? '');

    if (matchmakeabilityUnchanged && observabilityUnchanged) {
      return;
    }

    console.log(`[PavilionCoordinator] 🔁 matchmakeability/observability update (${reason})`, {
      from: {
        matchmakeable: current.matchmakeable,
        blockReason: previousReason,
        processPolicyViolationCount: current.processPolicyViolationCount ?? 0,
      },
      to: {
        ...result,
        processPolicyViolationCount: violationCount,
        lastBlockedProcessName,
      },
    });

    this.hostMatchmakingStore.updateState({
      ...current,
      matchmakeable: result.matchmakeable,
      matchmakeableBlockReason: result.blockReason,
      processPolicyViolationCount: violationCount,
      lastBlockedProcessName,
      lastBlockedReason,
    });
    this.sendHostConfigToAPI();
  }

  /**
   * Handle game launched from GameLaunchCoordinator
   * Sets gameState=ready and recalculates matchmakeability
   */
  private handleGameLaunched(): void {
    // Destructive admin exit in progress: the host is tearing down (guest-mode
    // exit / reboot handled by PlaycastService). Ignore any late or in-flight
    // game-launch event so we never drop the terminal gate and re-expose the host
    // to matchmaking while teardown is still running. The gate stays set for the
    // life of the process, which ends as part of teardown.
    if (this.isTerminalExit) {
      console.log('[PavilionCoordinator] ⛔ Ignoring game launched — terminal exit in progress (host_exiting)');
      return;
    }

    console.log('[PavilionCoordinator] 📤 Handling game launched');

    // Clear restart guard — game has successfully relaunched
    if (this.isRestarting) {
      console.log('[PavilionCoordinator] 🔄 Restart complete — game relaunched');
      this.isRestarting = false;
    }

    // Clear macro-retry guard — relaunch from a macro retry has reached gameLaunched
    if (this.isMacroRestarting) {
      console.log('[PavilionCoordinator] 🔄 Macro retry restart complete — game relaunched');
      this.isMacroRestarting = false;
    }

    // Clear campaign switch guard — new game has launched
    if (this.isSwitchingCampaign) {
      console.log('[PavilionCoordinator] 🔄 Campaign switch complete — new game launched');
      this.isSwitchingCampaign = false;
    }

    // Clear safety timeout for both restart and normal campaign switch paths
    this.clearPrewarmingSafetyTimeout();

    // Bind the next cooldown trigger evaluation to a fresh launch instance.
    // We deliberately do NOT clear an active cooldown here: a restart of the
    // same campaign should preserve the running cooldown clock (Hitman: contract
    // timer continues across the host's internal restart). If a different
    // campaign was assigned, `campaignAssigned` already cleared the cooldown.
    this.currentLaunchInstanceId = generateLaunchInstanceId();

    // Set game state to ready first, then calculate matchmakeability
    this.hostMatchmakingStore.updateState({
      ...this.hostMatchmakingStore.currentState,
      gameState: 'ready',
      gameStateChangedAt: Date.now(),
    });

    // Apply 'onGameLaunched' cooldown trigger. `maybeStartCooldown` is a no-op
    // if a cooldown is already active (e.g. across a restart).
    this.maybeStartCooldown('onGameLaunched');

    const result = this.calculateMatchmakeabilityResult();

    // Safety check: game launched but no campaign means an inconsistent state.
    // This can happen when a campaign switch failure clears campaignId but the
    // game still launches late. Force to idle instead of staying stuck at ready.
    if (!result.matchmakeable && !this.hostMatchmakingStore.currentState.campaignId) {
      console.error('[PavilionCoordinator] ⚠️ Game launched with no campaign — inconsistent state, forcing idle');
      this.hostMatchmakingStore.updateState({
        ...this.hostMatchmakingStore.currentState,
        gameState: 'idle',
        gameStateChangedAt: Date.now(),
        matchmakeable: false,
        matchmakeableBlockReason: null,
      });
      this.sendHostConfigToAPI();
      return;
    }

    this.hostMatchmakingStore.updateState({
      ...this.hostMatchmakingStore.currentState,
      matchmakeable: result.matchmakeable,
      matchmakeableBlockReason: result.blockReason,
    });

    // Send HOST_CONFIG to API with updated state
    this.sendHostConfigToAPI();

    console.log('[PavilionCoordinator] ✅ Game launched, matchmakeable:', result.matchmakeable, 'blockReason:', result.blockReason);
  }

  /**
   * Handle game closed event from GameLaunchCoordinator
   * Clears campaign, sets game state to idle, and notifies API
   */
  private handleGameClosed(): void {
    console.log('[PavilionCoordinator] 📤 Handling game closed');

    if (this.isRestarting) {
      // During a restart, don't clear campaign — game is being relaunched with the same config
      console.log('[PavilionCoordinator] 🔄 Game closed during restart — keeping campaign, awaiting relaunch');
      this.hostMatchmakingStore.updateState({
        ...this.hostMatchmakingStore.currentState,
        gameState: 'prewarming',
        gameStateChangedAt: Date.now(),
        matchmakeable: false,
      });
      return;
    }

    if (this.isMacroRestarting) {
      // StatefulMacroCoordinator is mid-retry: it called stopGame to terminate the
      // failed launch and will relaunch with the SAME campaignId once gameClosed
      // resolves. Preserve campaign + go prewarming so admin-dash continues to
      // show the campaign assignment instead of "none" during the retry window.
      console.log('[PavilionCoordinator] 🔄 Game closed during macro retry — keeping campaign, awaiting relaunch');
      this.hostMatchmakingStore.updateState({
        ...this.hostMatchmakingStore.currentState,
        gameState: 'prewarming',
        gameStateChangedAt: Date.now(),
        matchmakeable: false,
      });
      return;
    }

    if (this.isSwitchingCampaign) {
      // Defensive: handleGameLaunched normally clears `isSwitchingCampaign` once the
      // gameReady chain completes. If the flag is still set but the game already
      // reached `ready`, the switch is effectively done and the flag is stale (the
      // race surfaces under CI load when multiple async listeners interleave).
      // Treat this as a normal close so the host returns to idle instead of
      // sticking in `prewarming` with no in-flight launch behind it.
      const currentGameState = this.hostMatchmakingStore.currentState.gameState;
      if (currentGameState === 'ready') {
        console.warn(
          '[PavilionCoordinator] ⚠️ isSwitchingCampaign was stale at close (state=ready); falling through to normal close',
        );
        this.isSwitchingCampaign = false;
      } else {
        // Genuine mid-switch close: Playjector terminated the old game before
        // launching the new one. Keep the campaign and stay in prewarming.
        console.log('[PavilionCoordinator] 🔄 Game closed during campaign switch — keeping campaign, awaiting new game');
        this.hostMatchmakingStore.updateState({
          ...this.hostMatchmakingStore.currentState,
          gameState: 'prewarming',
          gameStateChangedAt: Date.now(),
          matchmakeable: false,
        });
        return;
      }
    }

    // Normal close: clear campaign and go idle
    this.managers.cooldown.clear('game_closed');
    this.currentLaunchInstanceId = null;
    this.hostMatchmakingStore.updateState({
      ...this.hostMatchmakingStore.currentState,
      campaignId: undefined,
      gameState: 'idle',
      gameStateChangedAt: Date.now(),
      matchmakeable: false, // Can't matchmake without a game running
      cooldownEndsAt: undefined,
    });

    // Notify API of updated state
    this.sendHostConfigToAPI();

    console.log('[PavilionCoordinator] ✅ Game closed state sent to API');
  }

  /**
   * Handle API-initiated campaign clear.
   *
   * When the API sends MATCHMAKER_CAMPAIGN_SWITCH with an empty campaignId,
   * MatchmakingManager clears campaignStore and emits campaignCleared.
   * We must also update hostMatchmakingStore, stop the game, and notify the API.
   */
  private handleCampaignCleared(): void {
    console.log('[PavilionCoordinator] Handling campaign cleared by API');

    this.isSwitchingCampaign = false;
    this.isRestarting = false;
    this.isMacroRestarting = false;

    this.managers.cooldown.clear('campaign_cleared');
    this.currentLaunchInstanceId = null;

    this.hostMatchmakingStore.updateState({
      ...this.hostMatchmakingStore.currentState,
      campaignId: undefined,
      gameState: 'idle',
      gameStateChangedAt: Date.now(),
      matchmakeable: false,
      cooldownEndsAt: undefined,
    });

    this.sendHostConfigToAPI();

    if (this.gameLaunchCoordinator) {
      this.gameLaunchCoordinator.stopGame(this.authId);
    }

    console.log('[PavilionCoordinator] ✅ Campaign cleared — host is idle');
  }

  /**
   * Handle peer connection timeout from SessionCoordinator
   *
   * If no client peer connects within 30 seconds after sending MATCHMAKER_HOST_READY,
   * the host should re-enter the matchmaking pool with the running game.
   *
   * NOTE: SessionCoordinator has already reset its own state (closed stale WebRTC peer,
   * set status to idle) before emitting this event. We only need to handle matchmaking.
   *
   * @param invitationId - The invitation ID that timed out
   */
  private handlePeerConnectionTimeout(invitationId?: string): void {
    console.warn('[PavilionCoordinator] ⏰ Peer connection timeout for invitation:', invitationId);

    // Clear the timed-out invitation from our local state
    this.invitationStore.updateState({
      ...this.invitationStore.currentState,
      id: '',
    });

    this.matchmakingStore.updateState({
      ...this.matchmakingStore.currentState,
      status: 'idle',
      matchId: undefined,
    });

    // Recalculate matchmakeability from current state (don't assume true)
    const result = this.calculateMatchmakeabilityResult();
    this.hostMatchmakingStore.updateState({
      ...this.hostMatchmakingStore.currentState,
      matchmakeable: result.matchmakeable,
      matchmakeableBlockReason: result.blockReason,
    });

    // Re-send HOST_CONFIG to re-enter matchmaking pool (if matchmakeable)
    console.log('[PavilionCoordinator] 📤 Re-entering matchmaking pool after peer timeout, matchmakeable:', result.matchmakeable, 'blockReason:', result.blockReason);
    this.sendHostConfigToAPI();

    console.log('[PavilionCoordinator] ✅ Re-registered with matchmaker after peer timeout');
  }

  /**
   * Handle stream stopped event from SessionCoordinator
   *
   * When a stream stops (peer disconnected, error, manual stop), re-enter the matchmaking
   * pool if the game is still running. This ensures the host doesn't stay in a state where
   * it has a running game but isn't matchmakeable.
   *
   * @param reason - The reason the stream stopped
   */
  private handleStreamStopped(reason?: string): void {
    console.log('[PavilionCoordinator] 📤 Handling stream stopped:', reason);

    const currentState = this.hostMatchmakingStore.currentState;

    // Clear invitation since the session is over
    this.invitationStore.updateState({
      ...this.invitationStore.currentState,
      id: '',
    });

    this.matchmakingStore.updateState({
      ...this.matchmakingStore.currentState,
      status: 'idle',
      matchId: undefined,
    });

    // Recalculate matchmakeability from current state
    const result = this.calculateMatchmakeabilityResult();
    const previousReason = currentState.matchmakeableBlockReason ?? null;
    if (
      result.matchmakeable !== currentState.matchmakeable ||
      result.blockReason !== previousReason
    ) {
      console.log('[PavilionCoordinator] 🔄 Matchmakeability changed after stream stop:', result);
      this.hostMatchmakingStore.updateState({
        ...this.hostMatchmakingStore.currentState,
        matchmakeable: result.matchmakeable,
        matchmakeableBlockReason: result.blockReason,
      });
    }

    if (currentState.gameState === 'ready') {
      // Re-send HOST_CONFIG to re-enter matchmaking pool
      this.sendHostConfigToAPI();
      console.log('[PavilionCoordinator] ✅ Re-entered matchmaking pool after stream stop, matchmakeable:', result.matchmakeable, 'blockReason:', result.blockReason);
    } else {
      console.log('[PavilionCoordinator] ℹ️ Game not running after stream stop, staying idle');
    }
  }

  /**
   * Handle all players leaving while hosting
   *
   * Replaces the legacy Ad Mode restart flow in playerEvents.ts.
   * When the last player disconnects:
   * 1. Invalidate the matchmaking session
   * 2. Check campaign config for restartOnDisconnect
   * 3. If restart: set prewarming state → stop stream → relaunch game
   * 4. If no restart: stop stream → handleStreamStopped re-enters pool
   */
  private async handleAllPlayersLeft(): Promise<void> {
    const campaignId = this.getCampaignId();
    const matchId = this.getMatchId();
    if (!campaignId)
    {
      console.error("[PavilionCoordinator] No campaign id when all playes have left.");
      Sentry.logger.error("[PavilionCoordinator] No campaign id when all playes have left.");
      if (matchId) {
        this.sendInvalidateSession(matchId, "No valid campaign available on player leave.");
      }
      return;
    }

    console.log('[PavilionCoordinator] Querying new updated campaign config before relaunch: ', campaignId);
    Sentry.logger.info('[PavilionCoordinator] Querying new updated campaign config before relaunch: ', { campaignId });
    const updatedConfig = await this.fetchCampaignConfig(campaignId);
    if (updatedConfig)
    {
        console.log('[PavilionCoordinator] 📦 Raw config from API:', JSON.stringify(updatedConfig, null, 2));
        Sentry.logger.info('[PavilionCoordinator] 📦 Raw config from API:', { config: JSON.stringify(updatedConfig, null, 2) });

        // Update campaign store with fetched config
        this.campaignStore.updateState({
          ...this.campaignStore.currentState,
          config: updatedConfig,
          configFetched: true,
        });

        console.log('[PavilionCoordinator] ✅ Campaign config stored:', updatedConfig.displayName);
        Sentry.logger.info('[PavilionCoordinator] ✅ Campaign config stored:', { displayName: updatedConfig.displayName });
    }

    const campaignConfig = this.getCampaignConfig();
    const shouldRestart = campaignConfig?.restartOnDisconnect !== false;

    console.log('[PavilionCoordinator] 🚪 Handling all players left:', {
      shouldRestart,
      matchId,
      campaignId,
      hasSessionCoordinator: !!this.sessionCoordinator,
    });
    Sentry.logger.info('[PavilionCoordinator] 🚪 Handling all players left:', {
      shouldRestart,
      matchId,
      campaignId,
      hasSessionCoordinator: !!this.sessionCoordinator,
    });

    // Invalidate the current matchmaking session so the API cleans up
    if (matchId) {
      this.sendInvalidateSession(matchId, 'All players disconnected');
    }

    if (shouldRestart && campaignId) {
      // RESTART path: terminate game and relaunch with same campaign
      console.log('[PavilionCoordinator] 🔄 Restart enabled — restarting game for campaign:', campaignId);
      Sentry.logger.info('[PavilionCoordinator] 🔄 Restart enabled — restarting game for campaign:', { campaignId });

      // Set restart guard to prevent handleGameClosed from clearing campaign
      this.isRestarting = true;

      // The cooldown belongs to the campaign clock, not the launch instance —
      // a restart of the same campaign must preserve the running cooldown
      // (Hitman: contract timer keeps ticking across host-side restart).
      this.currentLaunchInstanceId = null;

      // Set prewarming state BEFORE stopping stream to prevent re-entering pool
      // (handleStreamStopped checks gameState — if not 'ready', it won't re-enter)
      this.hostMatchmakingStore.updateState({
        ...this.hostMatchmakingStore.currentState,
        gameState: 'prewarming',
        gameStateChangedAt: Date.now(),
        matchmakeable: false,
        currentMatchInvitationId: undefined,
      });
      this.sendHostConfigToAPI();

      // Safety timeout: if host gets stuck in prewarming, force cleanup
      // Uses the same timeout as CampaignSwitchManager (base 480s + game-specific readyWaitTime)
      const readyWaitTimeMs = campaignConfig?.launchPackage?.readyWaitTimeMs || 0;
      this.startPrewarmingSafetyTimeout(campaignId, readyWaitTimeMs);

      // Stop stream (handleStreamStopped will fire but see gameState=prewarming → no pool re-entry)
      this.sessionCoordinator?.stopStream('host:all_players_left_restart');

      // Trigger game relaunch — GameLaunchCoordinator listens to campaignConfigLoaded
      // and will terminate the running game + relaunch with the same campaign config
      this.events.campaignConfigLoaded.emit({ campaignId });
    } else {
      // NO RESTART path: run exit macro, then stop stream, keep game running
      console.log('[PavilionCoordinator] ⏹️ Restart disabled — running exit macro then stopping stream');
      Sentry.logger.info('[PavilionCoordinator] ⏹️ Restart disabled — running exit macro then stopping stream');

      // Clear match ID so subsequent HOST_CONFIG messages don't carry the stale ID
      this.hostMatchmakingStore.updateState({
        ...this.hostMatchmakingStore.currentState,
        currentMatchInvitationId: undefined,
      });

      // Await exit macro before re-entering the matchmaking pool
      if (this.gameLaunchCoordinator) {
        await this.gameLaunchCoordinator.executeOnExitMacro();
      }

      // Stop stream — handleStreamStopped will fire and re-enter matchmaking pool
      // if the game is still running (calculateMatchmakeability checks game state)
      this.sessionCoordinator?.stopStream('host:all_players_left_no_restart');
    }
  }

  /**
   * Send MATCHMAKER_INVALIDATE_SESSION to the API
   */
  private sendInvalidateSession(invitationId: string, reason: string): void {
    console.log('[PavilionCoordinator] 📤 Invalidating session:', invitationId, reason);
    this.realtimeService.sendMessage(
      websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_INVALIDATE_SESSION, {
        invitationId,
        reason,
      })
    );
  }

  /**
   * Send MATCHMAKER_HOST_CONFIG message to API
   * Reports current host operational state to matchmaking system
   *
   * Merges dynamic state (from this coordinator) with static properties (from AppCoordinator)
   *
   * IMPORTANT: Only sends HostOperationalState fields. Does NOT send HostOrchestrationState
   * fields (desiredCampaign, switchRequestedAt) as those are managed by the API.
   */
  private sendHostConfigToAPI(): void {
    // Guard: Don't send if machineId is not yet initialized
    const machineId = machineIdState.currentState;
    if (!machineId) {
      console.log('[PavilionCoordinator] ⏸️ Skipping HOST_CONFIG send - machineId not yet initialized');
      return;
    }

    const currentState = this.hostMatchmakingStore.currentState;

    // Build HostOperationalState (what the host reports)
    // Explicitly exclude HostOrchestrationState fields (API-managed: desiredCampaign, switchRequestedAt)
    const stateToSend: HostMatchmakingState = {
      // Operational flags
      globalMatchmakingToggle: currentState.globalMatchmakingToggle,
      matchmakeable: currentState.matchmakeable,
      matchmakeableBlockReason: currentState.matchmakeableBlockReason ?? null,
      processPolicyViolationCount: currentState.processPolicyViolationCount,
      lastBlockedProcessName: currentState.lastBlockedProcessName,
      lastBlockedReason: currentState.lastBlockedReason,
      bandwidth: currentState.bandwidth,
      guestAccount: this.appCoordinator?.getGuestAccount() ?? currentState.guestAccount,
      lastUpdated: currentState.lastUpdated,
      currentMatchInvitationId: currentState.currentMatchInvitationId,

      // Current campaign & game state (what host is actually running)
      campaignId: currentState.campaignId,
      gameState: currentState.gameState,
      gameStateChangedAt: currentState.gameStateChangedAt,
      lastSwitchedToCampaign: currentState.lastSwitchedToCampaign,
      cooldownEndsAt: currentState.cooldownEndsAt,

      // Host type classification
      isCloudHost: this.appCoordinator?.getIsCloudHost() ?? currentState.isCloudHost,

      // DO NOT include: desiredCampaign, switchRequestedAt (API owns these)
      // NOTE: Gaming score is submitted via SUBMIT_SCORING_METRICS (server-computed, not in HOST_CONFIG)
    };

    // Sanity check: Warn if sending 'ready' state which indicates successful campaign launch
    // This helps identify unexpected HOST_CONFIG messages that might confuse the API
    if (stateToSend.gameState === 'ready' && stateToSend.matchmakeable === true) {
      console.log('[PavilionCoordinator] 🎯 Sending HOST_CONFIG with READY state (campaign success) - API will clear desiredCampaign');
    }

    console.log('[PavilionCoordinator] 📤 Sending HOST_CONFIG to API:', {
      gameState: stateToSend.gameState,
      campaignId: stateToSend.campaignId,
      matchmakeable: stateToSend.matchmakeable,
      matchmakeableBlockReason: stateToSend.matchmakeableBlockReason,
      cooldownEndsAt: stateToSend.cooldownEndsAt,
      isCloudHost: stateToSend.isCloudHost,
      guestAccount: stateToSend.guestAccount,
    });

    try {
      const message = websocket.generators.matchmaking(
        MatchmakingMessageType.MATCHMAKER_HOST_CONFIG,
        {
          machineId,
          state: stateToSend,
        }
      );

      this.realtimeService.sendMessage(message);
      console.log('[PavilionCoordinator] ✅ HOST_CONFIG sent');
    } catch (error) {
      console.error('[PavilionCoordinator] ❌ Failed to send HOST_CONFIG:', error);
      throw error;
    }
  }

  // ========================================
  // Public API - Registration (Setup Only)
  // ========================================

  /**
   * Register message handlers with the router
   *
   * This is called once during app initialization to connect this coordinator
   * to the MessageRouter. Individual handlers are private.
   */
  public registerMessageHandlers(router: IMessageRouter): void {
    router.register(MatchmakingMessageType.MATCHMAKER_STATE_UPDATE, (msg) => this.onMatchmakerStateUpdate(msg as MatchmakingMessage));
    router.register(MatchmakingMessageType.MATCHMAKER_HOST_MATCH_FOUND, (msg) => this.onHostMatchFound(msg as MatchmakingMessage));
    router.register(MatchmakingMessageType.MATCHMAKER_ERROR, (msg) => this.onMatchmakerError(msg as MatchmakingMessage));
    router.register(MatchmakingMessageType.MATCHMAKER_CAMPAIGN_SWITCH, (msg) => this.onCampaignSwitch(msg as MatchmakingMessage));
    router.register(MatchmakingMessageType.MATCHMAKER_INVITATION_UPDATED, (msg) => this.onInvitationUpdated(msg as MatchmakingMessage));
  }

  /**
   * Register HOST message handlers
   *
   * HOST messages come on a separate WebSocket channel and need their own routing.
   * Currently we only handle HOST_MACHINE_CREDENTIALS for Steam/Epic credentials.
   */
  public registerHostMessageHandlers(router: IMessageRouter): void {
    router.register('HOST_MACHINE_CREDENTIALS' as any, this.onMachineCredentials.bind(this));
  }

  // ========================================
  // Private Message Handlers (Delegate to Managers)
  // ========================================

  /**
   * Handle MATCHMAKER_STATE_UPDATE messages
   * Delegates to MatchmakingManager
   */
  private onMatchmakerStateUpdate(message: MatchmakingMessage): void {
    this.managers.matchmaking.handleStateUpdate(message);
  }

  /**
   * Handle MATCHMAKER_HOST_MATCH_FOUND messages
   * Delegates to MatchmakingManager
   */
  private onHostMatchFound(message: MatchmakingMessage): void {
    this.managers.matchmaking.handleHostMatchFound(message);
  }

  /**
   * Handle MATCHMAKER_ERROR messages
   * Delegates to MatchmakingManager
   */
  private onMatchmakerError(message: MatchmakingMessage): void {
    this.managers.matchmaking.handleError(message);
  }

  /**
   * Handle MATCHMAKER_CAMPAIGN_SWITCH messages
   * Delegates to MatchmakingManager
   */
  private onCampaignSwitch(message: MatchmakingMessage): void {
    this.managers.matchmaking.handleCampaignSwitch(message);
  }

  /**
   * Handle MATCHMAKER_INVITATION_UPDATED messages
   * Updates local invitation state when the API reports changes (e.g. invalidation)
   */
  private onInvitationUpdated(message: MatchmakingMessage): void {
    // MATCHMAKER_INVITATION_UPDATED has invitationId and invitation fields
    // but MatchmakingMessage union type doesn't expose them for all variants
    const payload = message as unknown as { invitationId?: string; invitation?: Invitation };

    console.log('[PavilionCoordinator] 📩 Invitation updated:', {
      invitationId: payload.invitationId,
      invalid: payload.invitation?.invalid,
    });

    if (payload.invitation) {
      this.setInvitation(payload.invitation);
    }
  }

  /**
   * Handle HOST_MACHINE_CREDENTIALS messages
   * Updates Steam/Epic credentials for the host
   */
  private onMachineCredentials(message: any): void {
    console.log('[PavilionCoordinator] 🔑 Machine credentials received:', {
      hasSteam: !!message.steam,
      hasEpic: !!message.epic,
    });

    // Extract Steam credentials if present
    if (message.steam && message.steam.username && message.steam.password) {
      console.log('[PavilionCoordinator] ✅ Updating Steam credentials:', {
        username: message.steam.username,
        hasPassword: !!message.steam.password,
      });

      this.credentialsStore.updateState({
        username: message.steam.username,
        password: message.steam.password,
      });

      // IMPORTANT: After receiving credentials, notify Playjector via GameLaunchCoordinator
      // This ensures Playjector has the latest credentials before game launch
      // The credentials must be sent via hostModeChanged before processLaunch
      console.log('[PavilionCoordinator] 🔄 Credentials updated - GameLaunchCoordinator will send to Playjector before next launch');
    } else {
      console.warn('[PavilionCoordinator] ⚠️  No Steam credentials in HOST_MACHINE_CREDENTIALS message');
    }

    // TODO: Handle Epic credentials when needed
  }

  // ========================================
  // Private Helper Methods
  // ========================================

  /**
   * Fetch campaign configuration using the campaign config service
   *
   * @param campaignId - The campaign ID to fetch config for
   * @returns Promise resolving to the campaign config, or null if fetch fails
   */
  private async fetchCampaignConfig(campaignId: string): Promise<HostCampaignConfig | null> {
    return await this.campaignConfigService.fetchConfig(campaignId);
  }

  /**
   * Re-read the assigned campaign's config from the API and refresh the cached
   * copy, WITHOUT touching the game-launch pipeline.
   *
   * Admin edits to an already-assigned campaign are written to Redis but never
   * pushed to the host, so the cached config can be arbitrarily stale. Callers
   * that must act on current settings (notably clamping a joining player's
   * input access) refresh first (#13862).
   *
   * Deliberately does NOT emit `campaignConfigLoaded`: GameLaunchCoordinator
   * treats that event as "relaunch the game", so emitting it here would restart
   * the game every time it is called.
   *
   * Concurrent callers share one in-flight request. Never rejects — a failed
   * refresh leaves the previously cached config in place.
   */
  public async refreshCampaignConfig(): Promise<void> {
    const campaignId = this.campaignStore.currentState.campaignId;
    if (!campaignId) return;

    if (this.campaignConfigRefresh) return this.campaignConfigRefresh;

    this.campaignConfigRefresh = (async () => {
      try {
        const config = await this.fetchCampaignConfig(campaignId);

        // Drop the result if the campaign changed while the request was in
        // flight — applying it would resurrect a config for a campaign the host
        // is no longer serving.
        if (!config || this.campaignStore.currentState.campaignId !== campaignId) return;

        this.campaignStore.updateState({
          ...this.campaignStore.currentState,
          config,
          configFetched: true,
        });
      } catch (err) {
        console.warn('[PavilionCoordinator] Campaign config refresh failed; keeping cached config', err);
        Sentry.logger.warn('[PavilionCoordinator] Campaign config refresh failed; keeping cached config', { err });
      } finally {
        this.campaignConfigRefresh = undefined;
      }
    })();

    return this.campaignConfigRefresh;
  }

  // ========================================
  // Public API - Getters
  // ========================================

  /**
   * Get current matchmaking status
   */
  public getMatchmakingStatus(): MatchmakingStatus {
    return this.matchmakingStore.currentState.status;
  }

  /**
   * Get current match ID (if a match has been found)
   */
  public getMatchId(): string | undefined {
    return this.matchmakingStore.currentState.matchId;
  }

  /**
   * Get current invitation details (if a match has been found)
   */
  public getInvitation(): InvitationState | undefined {
    const invitation = this.invitationStore.currentState;
    // Return undefined if invitation has been cleared (empty ID)
    return invitation.id ? invitation : undefined;
  }

  /**
   * Get current campaign ID (if assigned)
   */
  public getCampaignId(): string | undefined {
    return this.campaignStore.currentState.campaignId;
  }

  /**
   * Check if coordinator has an active campaign
   */
  public hasCampaign(): boolean {
    return !!this.campaignStore.currentState.campaignId;
  }

  /**
   * Get campaign configuration (if fetched)
   *
   * @returns Campaign config or undefined if not fetched yet
   */
  public getCampaignConfig(): HostCampaignConfig | undefined {
    return this.campaignStore.currentState.config;
  }

  /**
   * Check if campaign config has been fetched
   */
  public hasCampaignConfig(): boolean {
    return !!this.campaignStore.currentState.configFetched;
  }

  /**
   * Get campaign details (ID and reason)
   * Helper method for testing/debugging
   */
  public getCampaignDetails(): { campaignId?: string; reason?: string; config?: HostCampaignConfig } {
    return {
      campaignId: this.campaignStore.currentState.campaignId,
      reason: this.campaignStore.currentState.reason,
      config: this.campaignStore.currentState.config,
    };
  }

  /**
   * Get matchmakeable status
   */
  public getMatchmakeable(): boolean {
    return this.hostMatchmakingStore.currentState.matchmakeable;
  }

  /**
   * Get current game state
   */
  public getGameState(): HostGameState | undefined {
    return this.hostMatchmakingStore.currentState.gameState;
  }

  /**
   * Check if host is a guest account (ad mode)
   */
  public isGuestAccount(): boolean {
    return this.hostMatchmakingStore.currentState.guestAccount;
  }

  // ========================================
  // Public API - Actions (WRITE)
  // ========================================

  /**
   * Set matchmakeable status
   * Action method with event emission
   */
  public setMatchmakeable(matchmakeable: boolean): void {
    console.log('[PavilionCoordinator] Setting matchmakeable:', matchmakeable);

    this.hostMatchmakingStore.updateState({
      ...this.hostMatchmakingStore.currentState,
      matchmakeable,
    });

    this.events.matchmakeableChanged.emit({ matchmakeable });
  }

  /**
   * Notify the coordinator that a player has joined the running game.
   *
   * Wired from `PeerCoordinator.events.onPlayerJoined` in `coordinators/init.ts`.
   * Idempotent within a launch instance — only the **first** join per instance
   * starts the `'onFirstPlayerJoined'` cooldown timer (the manager enforces this
   * via `launchInstanceId`).
   */
  public notifyPlayerJoined(): void {
    this.maybeStartCooldown('onFirstPlayerJoined');
  }

  /**
   * Apply the campaign-configured cooldown for a given trigger event.
   * No-op unless the campaign config matches and we have a valid launch instance.
   *
   * If a cooldown is already active (e.g. carried across a restart), this is
   * a no-op — the running clock wins. Subsequent player joins / launches do
   * not extend or restart it.
   */
  private maybeStartCooldown(triggeringEvent: 'onGameLaunched' | 'onFirstPlayerJoined'): void {
    if (this.currentLaunchInstanceId === null) {
      return;
    }

    if (this.managers.cooldown.isInCooldown()) {
      return;
    }

    const campaignConfig = this.getCampaignConfig();
    if (!campaignConfig) {
      return;
    }

    if (campaignConfig.cooldownStartTrigger !== triggeringEvent) {
      return;
    }

    const periodMs = campaignConfig.cooldownPeriodMs ?? 0;
    if (periodMs <= 0) {
      return;
    }

    this.managers.cooldown.start({
      launchInstanceId: this.currentLaunchInstanceId,
      periodMs,
    });
  }

  /**
   * React to cooldown manager state transitions by mirroring `cooldownEndsAt`
   * into the operational state and recomputing matchmakeability. We deliberately
   * do NOT fold cooldown into `gameState` — the game lifecycle is independent of
   * the campaign-configured cooldown clock, and folding the two collides with
   * existing reconnect / restart paths that switch through `prewarming`. The
   * cooldown surface is `matchmakeableBlockReason: 'cooldown'` plus `cooldownEndsAt`.
   *
   * Authority split:
   * - `'started'` and `'expired'` are manager-/timer-driven. `handleCooldownChanged`
   *   is the authoritative writer: it recomputes matchmakeability against the
   *   current store snapshot and emits a fresh HOST_CONFIG.
   * - `'cleared'` is programmatic — the caller (campaign switch, game close,
   *   prewarming timeout, etc.) is mid-transition and will emit the authoritative
   *   HOST_CONFIG itself after it finishes updating the broader host state. We
   *   only mirror `cooldownEndsAt: undefined` into the store so the cleared
   *   value cannot be reused, and intentionally do NOT recompute matchmakeable
   *   or send HOST_CONFIG here — doing so would emit a transient
   *   `{ matchmakeable: true }` based on the pre-transition snapshot.
   */
  private handleCooldownChanged(event: CooldownChangedEvent): void {
    const current = this.hostMatchmakingStore.currentState;

    if (event.reason === 'cleared') {
      const needsCooldownReset = current.cooldownEndsAt !== undefined;
      const needsBlockReasonReset = current.matchmakeableBlockReason === 'cooldown';
      if (needsCooldownReset || needsBlockReasonReset) {
        this.hostMatchmakingStore.updateState({
          ...current,
          cooldownEndsAt: undefined,
          matchmakeableBlockReason: needsBlockReasonReset
            ? undefined
            : current.matchmakeableBlockReason,
        });
      }
      console.log('[PavilionCoordinator] ✅ Cooldown cleared by caller (caller will emit HOST_CONFIG)');
      return;
    }

    if (event.active) {
      const result = this.calculateMatchmakeabilityResult();
      this.hostMatchmakingStore.updateState({
        ...current,
        matchmakeable: result.matchmakeable,
        matchmakeableBlockReason: result.blockReason,
        cooldownEndsAt: event.cooldownEndsAt ?? undefined,
      });
      this.sendHostConfigToAPI();
      console.log('[PavilionCoordinator] ✅ Entered cooldown, cooldownEndsAt:', event.cooldownEndsAt);
      return;
    }

    const result = this.calculateMatchmakeabilityResult();
    this.hostMatchmakingStore.updateState({
      ...current,
      matchmakeable: result.matchmakeable,
      matchmakeableBlockReason: result.blockReason,
      cooldownEndsAt: undefined,
    });
    this.sendHostConfigToAPI();
    console.log('[PavilionCoordinator] ✅ Exited cooldown (expired)', {
      matchmakeable: result.matchmakeable,
      blockReason: result.blockReason,
    });
  }

  /**
   * Notify the coordinator that an admin session has started or ended.
   * Recalculates matchmakeability based on actual state and sends HOST_CONFIG to the API.
   */
  public notifyAdminSessionChanged(active: boolean): void {
    console.log('[PavilionCoordinator] Admin session changed:', active);
    this.isAdminSessionActive = active;

    const result = this.calculateMatchmakeabilityResult();
    this.hostMatchmakingStore.updateState({
      ...this.hostMatchmakingStore.currentState,
      matchmakeable: result.matchmakeable,
      matchmakeableBlockReason: result.blockReason,
    });

    this.events.matchmakeableChanged.emit({ matchmakeable: result.matchmakeable });
    this.sendHostConfigToAPI();
  }

  /**
   * Force a terminal, non-matchmakeable state after the admin exits with a
   * destructive action (reboot / exit Nonsole). Unlike keep_game — which removes
   * hosting status but lets the host re-enter the pool for the next player — a
   * destructive exit means the machine is being torn down (guest mode exit /
   * reboot handled by PlaycastService). The sticky `isTerminalExit` gate keeps the
   * host out of matchmaking through every subsequent recompute (stream teardown,
   * the trailing notifyAdminSessionChanged(false)) until the process is gone.
   */
  public enterTerminalExitState(reason: string): void {
    console.log('[PavilionCoordinator] Entering terminal exit state (non-matchmakeable):', reason);
    this.isTerminalExit = true;
    this.currentLaunchInstanceId = null;
    this.managers.cooldown.clear('terminal_exit');

    // Tear down the broadcast so the machine stops being reported as hosting.
    // The terminal gate is already set, so the stream-stopped recompute below
    // cannot re-enter the matchmaking pool.
    this.sessionCoordinator?.removeHostingStatus();

    const result = this.calculateMatchmakeabilityResult();
    this.hostMatchmakingStore.updateState({
      ...this.hostMatchmakingStore.currentState,
      matchmakeable: result.matchmakeable,
      matchmakeableBlockReason: result.blockReason,
      cooldownEndsAt: undefined,
    });

    this.events.matchmakeableChanged.emit({ matchmakeable: result.matchmakeable });
    this.sendHostConfigToAPI();
  }

  /**
   * Restart the active campaign game after an admin session ends with restart_game.
   * Reuses the same relaunch path as all-players-left restart while staying in Nonsole Mode.
   */
  public async restartGameForAdminExit(): Promise<void> {
    const campaignId = this.getCampaignId();

    // Invalidate the active match so the matchmaker clears stale server-side
    // session data when the admin leaves (mirrors the all-players-left restart
    // path). The local stream is torn down below via stopStream, but that does
    // not reach the API on its own.
    const matchId = this.getMatchId();
    if (matchId) {
      this.sendInvalidateSession(matchId, 'Admin session ended — game restart');
    }

    if (!campaignId) {
      // No campaign to relaunch: terminate the game AND tear down the broadcast
      // so the machine stops being reported as hosting. Without this the stream
      // keeps running and the host still appears as hosting after admin exit
      // (the relaunch path below stops the stream via stopStream, but this early
      // return skipped it).
      console.warn('[PavilionCoordinator] Admin exit restart: no campaign — terminating game and clearing hosting status');
      Sentry.logger.warn('[PavilionCoordinator] Admin exit restart: no campaign — terminating game and clearing hosting status');
      this.gameLaunchCoordinator?.stopGame(this.authId);
      this.sessionCoordinator?.removeHostingStatus();
      return;
    }

    console.log('[PavilionCoordinator] Admin exit restart — relaunching campaign:', campaignId);
    Sentry.logger.info('[PavilionCoordinator] Admin exit restart — relaunching campaign:', { campaignId });

    // Enter prewarming synchronously (before the async config refetch) so that
    // the notifyAdminSessionChanged(false) call AdminPeerHandler fires right
    // after this method cannot observe gameState 'ready' and briefly re-enter
    // the matchmaking pool while the relaunch is in flight.
    this.isRestarting = true;
    this.currentLaunchInstanceId = null;

    this.hostMatchmakingStore.updateState({
      ...this.hostMatchmakingStore.currentState,
      gameState: 'prewarming',
      gameStateChangedAt: Date.now(),
      matchmakeable: false,
      currentMatchInvitationId: undefined,
    });
    this.sendHostConfigToAPI();

    // Refresh the campaign config before relaunch so the game does not restart
    // with stale or missing settings (mirrors handleAllPlayersLeft). The
    // GameLaunchCoordinator consumes the refreshed store when campaignConfigLoaded
    // is emitted below. A refetch failure (null OR a rejected fetch) must NOT
    // break the teardown/relaunch contract — we fall back to the existing cached
    // config and still proceed to stopStream + campaignConfigLoaded.
    let updatedConfig: HostCampaignConfig | null = null;
    try {
      updatedConfig = await this.fetchCampaignConfig(campaignId);
    } catch (err) {
      console.warn('[PavilionCoordinator] Admin exit restart — config refetch failed; reusing existing config', err);
      Sentry.logger.warn('[PavilionCoordinator] Admin exit restart — config refetch failed; reusing existing config', { err });
    }
    if (updatedConfig) {
      this.campaignStore.updateState({
        ...this.campaignStore.currentState,
        config: updatedConfig,
        configFetched: true,
      });
      console.log('[PavilionCoordinator] Admin exit restart — refreshed campaign config:', updatedConfig.displayName);
      Sentry.logger.info('[PavilionCoordinator] Admin exit restart — refreshed campaign config:', { displayName: updatedConfig.displayName });
    } else {
      console.warn('[PavilionCoordinator] Admin exit restart — config refetch returned null; reusing existing config');
      Sentry.logger.warn('[PavilionCoordinator] Admin exit restart — config refetch returned null; reusing existing config');
    }

    const readyWaitTimeMs = this.getCampaignConfig()?.launchPackage?.readyWaitTimeMs || 0;
    this.startPrewarmingSafetyTimeout(campaignId, readyWaitTimeMs);

    this.sessionCoordinator?.stopStream('admin_exit_restart');
    this.events.campaignConfigLoaded.emit({ campaignId });
  }

  /**
   * Handle watcher mismatch — trigger a clean game restart.
   *
   * Called by the ScheduledScreenshotWatcher (via init.ts) when the idle
   * screen hash diverges beyond tolerance. The watcher only runs while no
   * stream is active, so we skip the stopStream step (unlike admin-exit restart).
   */
  public handleWatcherMismatch(campaignId: string): void {
    console.log('[PavilionCoordinator] Watcher mismatch — restarting game:', campaignId);
    Sentry.logger.warn('[PavilionCoordinator] Watcher mismatch — restarting game', { campaignId });

    this.isRestarting = true;
    this.currentLaunchInstanceId = null;

    this.hostMatchmakingStore.updateState({
      ...this.hostMatchmakingStore.currentState,
      gameState: 'prewarming',
      gameStateChangedAt: Date.now(),
      matchmakeable: false,
      currentMatchInvitationId: undefined,
    });
    this.sendHostConfigToAPI();

    const readyWaitTimeMs = this.getCampaignConfig()?.launchPackage?.readyWaitTimeMs || 0;
    this.startPrewarmingSafetyTimeout(campaignId, readyWaitTimeMs);

    this.gameLaunchCoordinator?.stopGame(this.authId);
    this.events.campaignConfigLoaded.emit({ campaignId });
  }

  /**
   * Set game state
   * Action method with event emission
   */
  public setGameState(gameState: HostGameState): void {
    console.log('[PavilionCoordinator] Setting game state:', gameState);

    this.hostMatchmakingStore.updateState({
      ...this.hostMatchmakingStore.currentState,
      gameState,
    });

    this.events.gameStateChanged.emit({ gameState });
  }

  /**
   * Clear current campaign
   * Action method with event emission
   */
  public clearCampaign(): void {
    console.log('[PavilionCoordinator] Clearing campaign');

    this.campaignStore.updateState({
      campaignId: undefined,
      reason: undefined,
    });

    this.events.campaignCleared.emit();
  }

  /**
   * Get Steam credentials (for PlayjectorCoordinator)
   * Returns empty strings if no credentials available
   */
  public getCredentials(): { username: string; password: string } {
    return this.credentialsStore.currentState;
  }

  /**
   * Get host matchmaking state (for GameLaunchCoordinator)
   */
  public getHostMatchmakingState(): HostOperationalState {
    return this.hostMatchmakingStore.currentState;
  }

  /**
   * Get the current launch instance id.
   *
   * Set when the game becomes ready (via `handleGameLaunched`) and cleared on
   * game close, campaign switch, restart, or safety timeout. Used by the
   * StatefulMacroCoordinator to tag MACRO_WALK_REQUEST messages with a
   * host-owned identifier so the API can drop late/duplicated responses.
   *
   * Returns `null` if no launch is currently active. Consumers MUST tolerate
   * null and re-derive on the next `gameLaunched` if they need a fresh id.
   */
  public getCurrentLaunchInstanceId(): string | null {
    return this.currentLaunchInstanceId;
  }

  /**
   * Get auth ID (for PlayjectorCoordinator)
   *
   * TODO-STRANGLER-FIG: Temporary method until IdentityCoordinator is created.
   * In the future, IdentityCoordinator will own authId and emit events when it changes.
   */
  public getAuthId(): string {
    return this.authId;
  }

  /**
   * Mark the host as mid-macro-retry. Called by `StatefulMacroCoordinator`
   * immediately before it invokes `gameLaunchCoordinator.stopGame()` on the
   * retry path so the resulting `gameClosed` event does NOT clear the
   * campaign in `handleGameClosed()`. The flag is cleared on the next
   * successful `gameLaunched` (and in the same idle-resetting paths that
   * clear `isRestarting` / `isSwitchingCampaign`).
   *
   * Intentionally a no-op shape (boolean flip): the macro coordinator owns
   * the relaunch sequencing; we only need PavilionCoordinator to keep its
   * hands off `campaignId` for the duration of the stop → relaunch window.
   */
  public beginMacroRetryRestart(): void {
    this.isMacroRestarting = true;
  }

  /**
   * Update auth ID
   *
   * TODO-STRANGLER-FIG: Temporary method, called by legacy adapter in init.ts.
   * Should be replaced by listening to IdentityCoordinator.events.authenticated.
   */
  public updateAuthId(authId: string): void {
    if (this.authId !== authId) {
      this.authId = authId;
      console.log('[PavilionCoordinator] Auth ID updated:', authId);
    }
  }

  /**
   * Check if invitation is currently active (not invalid)
   */
  public isInvitationActive(): boolean {
    const invitation = this.invitationStore.currentState;
    return !!invitation.id && !invitation.invalid;
  }

  /**
   * Get invitation owner profile ID
   */
  public getInvitationOwner(): string | undefined {
    const invitation = this.invitationStore.currentState;
    return invitation.owner.profileId || undefined;
  }

  /**
   * Check if Steam guest login is enabled
   */
  public isSteamGuestLoginEnabled(): boolean {
    const invitation = this.invitationStore.currentState;
    if (invitation.type === 'cafe' && invitation.manifest) {
      return invitation.manifest.useSteamGuestLogin ?? false;
    }
    return false;
  }

  /**
   * Check if host is matchmakeable
   * TODO: Wire to actual hostMatchmaking store
   */
  public isMatchmakeable(): boolean {
    return this.hostMatchmakingStore.currentState.matchmakeable;
  }

  // ========================================
  // Public API - Actions (for Testing & Integration)
  // ========================================

  // TODO-STRANGLER-FIG: The following methods are temporary for MVP testing.
  // In production, invitation state would be updated via:
  // - State store replication from IPC/WebSocket messages
  // - CafeInvitationManager listening to invitation store changes
  // These methods should be removed once full state store integration is complete.

  /**
   * Set invitation state
   *
   * In production, this would be updated via state store replication.
   * For MVP/testing, we expose this method to drive invitation flows.
   *
   * @deprecated TODO-STRANGLER-FIG: Remove after wiring to real state stores
   */
  public setInvitation(invitation: Invitation): void {
    const prev = this.previousInvitation;

    // Update store
    this.invitationStore.updateState(invitation);

    // Emit change events based on what changed
    if (prev && invitation) {
      // Check for steam guest login changes (cafe type only)
      const prevSteamLogin =
        prev.type === 'cafe' && prev.manifest ? (prev.manifest.useSteamGuestLogin ?? false) : false;
      const newSteamLogin =
        invitation.type === 'cafe' && invitation.manifest ? (invitation.manifest.useSteamGuestLogin ?? false) : false;
      const invitationIdChanged = prev.id !== invitation.id;

      if (prevSteamLogin !== newSteamLogin || invitationIdChanged) {
        this.events.steamGuestLoginChanged.emit({ enabled: newSteamLogin });
      }

      // Check for owner changes
      if (prev.owner?.profileId && invitation.owner?.profileId) {
        if (prev.owner.profileId !== invitation.owner.profileId) {
          this.events.lobbyOwnerChanged.emit({
            oldOwnerId: prev.owner.profileId,
            newOwnerId: invitation.owner.profileId,
          });
        }
      }

      // Check for invalidation
      if (!prev.invalid && invitation.invalid) {
        this.events.invitationInvalidated.emit({
          invitationId: invitation.id,
          reason: 'invalidated',
        });
      }
    } else if (!prev && invitation) {
      // First invitation - emit steam guest login if present (cafe type only)
      const steamLogin =
        invitation.type === 'cafe' && invitation.manifest ? (invitation.manifest.useSteamGuestLogin ?? false) : false;
      this.events.steamGuestLoginChanged.emit({ enabled: steamLogin });
    }

    // Store for next comparison
    this.previousInvitation = invitation ? { ...invitation } : null;
  }

  /**
   * Set match found with invitation details
   * Convenience method for testing
   *
   * @deprecated TODO-STRANGLER-FIG: Remove after wiring to real state stores
   */
  public setMatchFound(matchId: string, invitation: Invitation): void {
    this.matchmakingStore.updateState({ status: 'found', matchId });
    this.events.matchFound.emit({ matchId }); // Emit match found first
    this.setInvitation(invitation); // Then update invitation (triggers steam login event)
  }

  /**
   * Simulate invitation invalidation (for testing)
   *
   * In production, this would be triggered by CafeInvitationManager
   * listening to invitation state changes.
   *
   * @deprecated TODO-STRANGLER-FIG: Remove after CafeInvitationManager is fully wired
   */
  public simulateInvitationInvalidated(invitationId: string, reason?: string): void {
    // Clear invitation (reset to empty state)
    this.invitationStore.updateState({
      id: '',
      type: 'cafe',
      invalid: false,
      linkExpiry: '',
      maxLinkUse: -1,
      authRequired: false,
      visibility: 'public',
      owner: { avatar: '', profileId: '', username: '' },
      manifest: { players: [], matchmakeToPublicMachine: false },
    });

    // Emit event
    this.events.invitationInvalidated.emit({ invitationId, reason });
  }

  /**
   * Simulate soft kick expiration (for testing)
   *
   * In production, this would be triggered by CafeInvitationManager.
   *
   * @deprecated TODO-STRANGLER-FIG: Remove after CafeInvitationManager is fully wired
   */
  public simulateSoftKickExpired(invitationId: string): void {
    // Clear invitation (reset to empty state)
    this.invitationStore.updateState({
      id: '',
      type: 'cafe',
      invalid: false,
      linkExpiry: '',
      maxLinkUse: -1,
      authRequired: false,
      visibility: 'public',
      owner: { avatar: '', profileId: '', username: '' },
      manifest: { players: [], matchmakeToPublicMachine: false },
    });

    // Emit event
    this.events.softKickExpired.emit({ invitationId });
  }

  // ========================================
  // Prewarming Safety Timeout
  // ========================================

  /**
   * Start a safety timeout for the prewarming state during restart.
   * If the game doesn't relaunch within the timeout, force cleanup
   * to prevent the host from being permanently stuck in prewarming.
   */
  private startPrewarmingSafetyTimeout(campaignId: string, readyWaitTimeMs: number): void {
    this.clearPrewarmingSafetyTimeout();

    const totalTimeoutMs = PREWARMING_SAFETY_BASE_TIMEOUT_MS + readyWaitTimeMs;
    console.log(`[PavilionCoordinator] ⏱️ Starting prewarming safety timeout: ${totalTimeoutMs}ms for campaign ${campaignId}`);

    this.prewarmingSafetyTimer = setTimeout(() => {
      console.error('[PavilionCoordinator] ⚠️ Prewarming safety timeout expired — forcing cleanup for campaign:', campaignId);

      // Clear all guards
      this.isRestarting = false;
      this.isSwitchingCampaign = false;
      this.isMacroRestarting = false;
      this.prewarmingSafetyTimer = null;

      // Clear campaign store (prevents stale getCampaignId() after timeout)
      this.campaignStore.updateState({
        campaignId: undefined,
        reason: undefined,
        config: undefined,
        configFetched: false,
      });

      // Force back to idle state — game failed to restart in time
      this.managers.cooldown.clear('prewarming_safety_timeout');
      this.currentLaunchInstanceId = null;
      this.hostMatchmakingStore.updateState({
        ...this.hostMatchmakingStore.currentState,
        gameState: 'idle',
        gameStateChangedAt: Date.now(),
        matchmakeable: false,
        campaignId: undefined,
        cooldownEndsAt: undefined,
      });

      this.sendHostConfigToAPI();
    }, totalTimeoutMs);
  }

  /**
   * Clear the prewarming safety timeout (called when game successfully launches)
   */
  private clearPrewarmingSafetyTimeout(): void {
    if (this.prewarmingSafetyTimer) {
      clearTimeout(this.prewarmingSafetyTimer);
      this.prewarmingSafetyTimer = null;
      console.log('[PavilionCoordinator] ⏱️ Prewarming safety timeout cleared');
    }
  }
}

// ========================================
// Helpers
// ========================================

/**
 * Generate a fresh launch instance id. Bound to a single successful launch macro
 * completion; cleared on game close, campaign switch, restart, or safety timeout.
 */
const generateLaunchInstanceId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID (older Jest, some Node versions).
  // This is non-cryptographic; the id only needs to be unique within a host process.
  return `launch-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

// ========================================
// Singleton Instance
// ========================================

/**
 * NOTE: The pavilionCoordinator singleton is now created in coordinators/init.ts
 * with proper service injection. Import it from there:
 *
 * ```typescript
 * import { pavilionCoordinator } from './coordinators/init';
 * ```
 */
